import { useState, useRef, useEffect } from 'react'
import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { useToastStore } from '@/stores/toastStore'
import { useEdgeBackSwipe } from '@/hooks/useEdgeBackSwipe'
import {
  ArrowLeft,
  MoreHorizontal,
  Pencil,
  Plus,
  Calendar,
  Clock,
  Repeat,
  FileText,
  Save,
  Sparkles,
  Loader2,
  Target,
  Share2,
  X,
  Bell,
} from 'lucide-react'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { SubtaskItem } from './SubtaskItem'
import { AttachmentSection } from './AttachmentSection'
import { MemoLinkModal } from './MemoLinkModal'
import { LinkedMemoPreview } from './LinkedMemoPreview'
import { DatePicker } from '@/components/ui/DatePicker'
import { Checkbox } from '@/components/ui/Checkbox'
import { getRepeatLabel } from '@/lib/repeatUtils'
import { getCategoryColorStyle } from '@/lib/categoryColors'
import { useTaskStore } from '@/stores/taskStore'
import { useCategoryStore } from '@/stores/categoryStore'
import { useTemplateStore } from '@/stores/templateStore'
import { useSettingsStore } from '@/stores/settingsStore'
import { useUIStore } from '@/stores/uiStore'
import { decomposeTaskWithAI, getAIConfig } from '@/services/aiService'
import { useFocusStore } from '@/stores/focusStore'
import { useWebShare } from '@/hooks/useWebShare'
import type { SubTask } from '@/lib/types'

interface TaskDetailProps {
  taskId: number
  onBack: () => void
  panelMode?: boolean
}

export function TaskDetail({ taskId, onBack, panelMode }: TaskDetailProps) {
  const task = useTaskStore((s) => s.tasks.find((t) => t.id === taskId))
  const updateTask = useTaskStore((s) => s.updateTask)
  const toggleComplete = useTaskStore((s) => s.toggleComplete)
  const reorderSubtasks = useTaskStore((s) => s.reorderSubtasks)
  const categories = useCategoryStore((s) => s.categories)
  const addTemplate = useTemplateStore((s) => s.addTemplate)
  const aiEnabled = useSettingsStore((s) => s.settings.aiEnabled)
  const openTaskEditModal = useUIStore((s) => s.openTaskEditModal)
  const startFocus = useFocusStore((s) => s.startFocus)
  const { shareTask } = useWebShare()

  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [titleValue, setTitleValue] = useState(task?.title ?? '')
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [isAiDecomposing, setIsAiDecomposing] = useState(false)
  const [showMemoLink, setShowMemoLink] = useState(false)
  const [previewMemo, setPreviewMemo] = useState<import('@/lib/types').LinkedMemoRef | null>(null)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const moreMenuRef = useRef<HTMLDivElement>(null)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  )

  // 좌측 에지 백-스와이프 (전체 화면 모드에서만; 데스크톱 패널은 비활성)
  const { x: edgeX, swipeHandlers, enabled: edgeEnabled } = useEdgeBackSwipe({
    onBack,
    disabled: !!panelMode,
  })

  const taskTitle = task?.title
  useEffect(() => {
    if (taskTitle !== undefined) setTitleValue(taskTitle)
  }, [taskTitle])

  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus()
      titleInputRef.current.select()
    }
  }, [isEditingTitle])

  // Click-outside to close dropdowns
  useEffect(() => {
    if (!showMoreMenu && !showCategoryDropdown) return
    const handlePointerDown = (e: PointerEvent) => {
      if (showMoreMenu && moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMoreMenu(false)
      }
      if (showCategoryDropdown && categoryDropdownRef.current && !categoryDropdownRef.current.contains(e.target as Node)) {
        setShowCategoryDropdown(false)
      }
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [showMoreMenu, showCategoryDropdown])

  if (!task) {
    return (
      <div className="flex-1 flex items-center justify-center p-4">
        <p className="text-caption">작업을 찾을 수 없습니다.</p>
      </div>
    )
  }

  const currentCategory = categories.find((c) => c.id === task.categoryId)

  const handleTitleSave = () => {
    const trimmed = titleValue.trim()
    if (trimmed && trimmed !== task.title) {
      updateTask(taskId, { title: trimmed })
    } else {
      setTitleValue(task.title)
    }
    setIsEditingTitle(false)
  }

  const handleCategoryChange = (categoryId: number | null) => {
    updateTask(taskId, { categoryId })
    setShowCategoryDropdown(false)
  }

  const handleDueDateChange = (date: string) => {
    updateTask(taskId, { dueDate: date || undefined })
  }

  const handleToggleSubtask = (subtaskId: string) => {
    const updatedSubtasks = task.subtasks.map((st) =>
      st.id === subtaskId ? { ...st, isCompleted: !st.isCompleted } : st
    )
    updateTask(taskId, { subtasks: updatedSubtasks })
  }

  const handleUpdateSubtask = (subtaskId: string, title: string) => {
    const updatedSubtasks = task.subtasks.map((st) =>
      st.id === subtaskId ? { ...st, title } : st
    )
    updateTask(taskId, { subtasks: updatedSubtasks })
  }

  const handleDeleteSubtask = (subtaskId: string) => {
    const updatedSubtasks = task.subtasks.filter((st) => st.id !== subtaskId)
    updateTask(taskId, { subtasks: updatedSubtasks })
  }

  const handleAddSubtask = () => {
    const newSubtask: SubTask = {
      id: crypto.randomUUID(),
      title: '',
      isCompleted: false,
      sortOrder: task.subtasks.length,
      createdAt: new Date().toISOString(),
    }
    updateTask(taskId, { subtasks: [...task.subtasks, newSubtask] })
  }

  const handleMemoChange = (memo: string) => {
    updateTask(taskId, { memo: memo || undefined })
  }

  const handleSubtaskDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    reorderSubtasks(taskId, String(active.id), String(over.id))
  }

  const handleSaveAsTemplate = () => {
    addTemplate({
      name: task.title,
      description: task.memo,
      categoryId: task.categoryId,
      priority: task.priority,
      subtasks: task.subtasks.map((st, i) => ({ title: st.title, sortOrder: i })),
      repeat: task.repeat,
      memo: task.memo,
      isBuiltIn: false,
      createdAt: new Date().toISOString(),
    })
    setShowMoreMenu(false)
  }

  const handleLinkMemo = (memo: import('@/lib/types').LinkedMemoRef) => {
    if (!task) return
    const existing = task.linkedMemos || []
    if (existing.some((m) => m.memoSyncId === memo.memoSyncId)) return // 중복 방지
    updateTask(taskId, { linkedMemos: [...existing, memo] })
  }

  const handleUnlinkMemo = (memoSyncId: string) => {
    if (!task) return
    const updated = (task.linkedMemos || []).filter((m) => m.memoSyncId !== memoSyncId)
    updateTask(taskId, { linkedMemos: updated })
  }

  const handleAiDecompose = async () => {
    if (!task) return
    setIsAiDecomposing(true)
    try {
      const { provider, apiKey } = getAIConfig()
      const subtasks = await decomposeTaskWithAI(provider, apiKey, task.title, task.memo)
      const newSubtasks: SubTask[] = subtasks.map((st, i) => ({
        id: crypto.randomUUID(),
        title: st.title,
        isCompleted: false,
        sortOrder: task.subtasks.length + i,
        createdAt: new Date().toISOString(),
      }))
      updateTask(taskId, { subtasks: [...task.subtasks, ...newSubtasks] })
    } catch (error) {
      console.error('AI decompose failed:', error)
      useToastStore.getState().showToast(error instanceof Error ? error.message : 'AI 분해에 실패했습니다.', 'error')
    } finally {
      setIsAiDecomposing(false)
    }
  }

  const completedSubtasks = task.subtasks.filter((st) => st.isCompleted).length
  const totalSubtasks = task.subtasks.length
  const sortedSubtasks = [...task.subtasks].sort((a, b) => a.sortOrder - b.sortOrder)
  const subtaskIds = sortedSubtasks.map((st) => st.id)

  return (
    <motion.div
      className={clsx("flex-1 flex flex-col bg-surface-primary", !panelMode && "min-h-screen")}
      style={edgeEnabled ? { x: edgeX } : undefined}
      {...swipeHandlers}
    >
      {/* Breadcrumb (hidden in panel mode) */}
      {!panelMode && (
      <nav aria-label="경로" className="px-4 pt-3 pb-1">
        <ol className="flex items-center gap-1 text-xs text-disabled">
          <li><button type="button" onClick={onBack} className="hover:text-[var(--text-secondary)] transition-colors">할 일</button></li>
          <li aria-hidden="true">&gt;</li>
          {currentCategory && (
            <>
              <li className="truncate max-w-[100px]">{currentCategory.name}</li>
              <li aria-hidden="true">&gt;</li>
            </>
          )}
          <li className="text-heading font-medium truncate max-w-[180px]">{task.title}</li>
        </ol>
      </nav>
      )}

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-base">
        <button
          type="button"
          onClick={onBack}
          className="p-1.5 rounded-lg text-body hover-surface transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="relative" ref={moreMenuRef}>
          <button
            type="button"
            onClick={() => setShowMoreMenu(!showMoreMenu)}
            className="p-1.5 rounded-lg text-body hover-surface transition-colors"
          >
            <MoreHorizontal className="w-5 h-5" />
          </button>

          {showMoreMenu && (
            <div className="absolute top-full right-0 mt-1 w-48 bg-surface-primary rounded-xl border border-base shadow-e2 z-20 py-1">
              <button
                type="button"
                onClick={() => {
                  setShowMoreMenu(false)
                  openTaskEditModal(task)
                }}
                className="w-full text-left px-3 py-2 text-sm text-body hover-surface flex items-center gap-2"
              >
                <Pencil className="w-4 h-4" />
                수정
              </button>
              {task.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMoreMenu(false)
                    startFocus(taskId, task.title)
                  }}
                  className="w-full text-left px-3 py-2 text-sm text-body hover-surface flex items-center gap-2"
                >
                  <Target className="w-4 h-4 text-primary-500" />
                  집중 모드
                </button>
              )}
              {aiEnabled && (
                <button
                  type="button"
                  onClick={() => {
                    setShowMoreMenu(false)
                    handleAiDecompose()
                  }}
                  disabled={isAiDecomposing}
                  className="w-full text-left px-3 py-2 text-sm text-body hover-surface flex items-center gap-2 disabled:opacity-50"
                >
                  {isAiDecomposing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-purple-500" />
                  )}
                  AI 하위 작업 분해
                </button>
              )}
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                className="w-full text-left px-3 py-2 text-sm text-body hover-surface flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                템플릿으로 저장
              </button>
              <button
                type="button"
                onClick={async () => {
                  setShowMoreMenu(false)
                  await shareTask(task)
                }}
                className="w-full text-left px-3 py-2 text-sm text-body hover-surface flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                공유
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-6">
        {/* Category badge + Priority badge */}
        <div className="relative flex items-center gap-2 flex-wrap" ref={categoryDropdownRef}>
          <button
            type="button"
            onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
            className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium transition-colors"
            style={{
              backgroundColor: currentCategory
                ? `${currentCategory.color}20`
                : 'var(--color-surface-tertiary, rgb(244 244 245))',
              color: currentCategory?.color ?? 'var(--color-text-caption, #71717a)',
            }}
          >
            {currentCategory?.name ?? '미분류'}
          </button>
          {task.priority !== 'none' && (
            <span
              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
              style={{
                backgroundColor:
                  task.priority === 'high'
                    ? 'var(--priority-high-bg)'
                    : task.priority === 'medium'
                      ? 'var(--priority-medium-bg)'
                      : 'var(--priority-low-bg)',
                color:
                  task.priority === 'high'
                    ? 'var(--priority-high)'
                    : task.priority === 'medium'
                      ? 'var(--priority-medium)'
                      : 'var(--priority-low)',
              }}
            >
              {task.priority === 'high' ? '높음' : task.priority === 'medium' ? '중간' : '낮음'}
            </span>
          )}

          {/* Category dropdown */}
          {showCategoryDropdown && (
            <div className="absolute top-full left-0 mt-1 w-48 bg-surface-primary rounded-xl border border-base shadow-e2 z-20 py-1">
              <button
                type="button"
                onClick={() => handleCategoryChange(null)}
                className="w-full text-left px-3 py-2 text-sm text-body hover-surface"
              >
                미분류
              </button>
              {categories
                .filter((c) => !c.parentId)
                .sort((a, b) => a.sortOrder - b.sortOrder)
                .map((root) => {
                  const children = categories
                    .filter((c) => c.parentId === root.id)
                    .sort((a, b) => a.sortOrder - b.sortOrder)

                  return (
                    <div key={root.id}>
                      <button
                        type="button"
                        onClick={() => handleCategoryChange(root.id ?? null)}
                        className="w-full text-left px-3 py-2 text-sm hover-surface flex items-center gap-2"
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-full"
                          style={{ backgroundColor: getCategoryColorStyle(root.color) }}
                        />
                        <span className="text-heading">{root.name}</span>
                      </button>
                      {children.map((child) => (
                        <button
                          key={child.id}
                          type="button"
                          onClick={() => handleCategoryChange(child.id ?? null)}
                          className="w-full text-left pl-8 pr-3 py-2 text-sm hover-surface flex items-center gap-2"
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: getCategoryColorStyle(child.color) }}
                          />
                          <span className="text-heading">{child.name}</span>
                        </button>
                      ))}
                    </div>
                  )
                })}
            </div>
          )}
        </div>

        {/* Title */}
        <div className="flex items-start gap-3">
          <Checkbox
            checked={task.status === 'completed'}
            onChange={() => toggleComplete(taskId)}
          />
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              value={titleValue}
              onChange={(e) => setTitleValue(e.target.value)}
              onBlur={handleTitleSave}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTitleSave()
                if (e.key === 'Escape') {
                  setTitleValue(task.title)
                  setIsEditingTitle(false)
                }
              }}
              className="flex-1 text-xl font-bold bg-transparent border-none outline-none text-heading"
            />
          ) : (
            <h1
              onClick={() => setIsEditingTitle(true)}
              className={`flex-1 text-xl font-bold cursor-text ${task.status === 'completed'
                  ? 'line-through text-disabled'
                  : 'text-heading'
                }`}
            >
              {task.title}
            </h1>
          )}
        </div>

        {/* Subtasks */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-body">
              하위 작업
              {totalSubtasks > 0 && (
                <span className="ml-1.5 text-xs text-disabled">
                  {completedSubtasks}/{totalSubtasks}
                </span>
              )}
            </span>
          </div>

          {/* Subtask progress */}
          {totalSubtasks > 0 && (
            <div className="h-1.5 bg-surface-muted rounded-full overflow-hidden mb-3">
              <div
                className="h-full bg-primary-500 rounded-full transition-all duration-300"
                style={{
                  width: `${totalSubtasks > 0 ? (completedSubtasks / totalSubtasks) * 100 : 0}%`,
                }}
              />
            </div>
          )}

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSubtaskDragEnd}>
            <SortableContext items={subtaskIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-0.5">
                {sortedSubtasks.map((subtask) => (
                  <SubtaskItem
                    key={subtask.id}
                    subtask={subtask}
                    onToggle={handleToggleSubtask}
                    onUpdate={handleUpdateSubtask}
                    onDelete={handleDeleteSubtask}
                    sortable
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          <button
            type="button"
            onClick={handleAddSubtask}
            className="flex items-center gap-2 mt-2 px-2 py-1.5 text-sm text-primary-500 hover:text-primary-600 hover:bg-accent-primary rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            하위 작업 추가
          </button>
        </div>

        {/* Properties divider */}
        <div className="border-t border-base" />

        {/* Properties */}
        <div className="space-y-0">
          {/* Due Date */}
          <div className="flex items-center justify-between py-3 border-b border-base">
            <div className="flex items-center gap-3 text-sm text-body">
              <Calendar className="w-4 h-4" />
              <span>마감일</span>
            </div>
            <DatePicker
              value={task.dueDate}
              onChange={handleDueDateChange}
              placeholder="아니요"
            />
          </div>

          {/* Due Time */}
          {task.dueDate && (
            <div className="flex items-center justify-between py-3 border-b border-base">
              <div className="flex items-center gap-3 text-sm text-body">
                <Clock className="w-4 h-4" />
                <span>마감 시간</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={task.dueTime || ''}
                  onChange={(e) => updateTask(taskId, { dueTime: e.target.value || undefined })}
                  className="px-2 py-1 text-sm rounded-lg border border-strong bg-surface-secondary text-heading outline-none focus:border-primary-500 transition-colors"
                />
                {task.dueTime && (
                  <button
                    type="button"
                    onClick={() => updateTask(taskId, { dueTime: undefined })}
                    className="p-1 text-disabled hover:text-red-500 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Alarm */}
          <div className="flex items-center justify-between py-3 border-b border-base">
            <div className="flex items-center gap-3 text-sm text-body">
              <Bell className="w-4 h-4" />
              <span>알림</span>
            </div>
            <button
              type="button"
              onClick={() => openTaskEditModal(task)}
              className="text-sm text-caption hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              {task.alarm?.enabled ? '설정됨' : '아니요'}
            </button>
          </div>

          {/* Repeat */}
          <div className="flex items-center justify-between py-3 border-b border-base">
            <div className="flex items-center gap-3 text-sm text-body">
              <Repeat className="w-4 h-4" />
              <span>반복 작업</span>
            </div>
            <button
              type="button"
              onClick={() => openTaskEditModal(task)}
              className="text-sm text-caption hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
            >
              {getRepeatLabel(task.repeat)}
            </button>
          </div>

          {/* Memo */}
          <div className="py-3 border-b border-base">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3 text-sm text-body">
                <FileText className="w-4 h-4" />
                <span>메모</span>
              </div>
              {!task.memo && (
                <button
                  type="button"
                  onClick={() => handleMemoChange(' ')}
                  className="text-sm text-primary-500 hover:text-primary-600"
                >
                  추가
                </button>
              )}
            </div>
            {task.memo !== undefined && (
              <textarea
                value={task.memo}
                onChange={(e) => handleMemoChange(e.target.value)}
                placeholder="메모를 입력하세요..."
                rows={3}
                className="w-full mt-1 px-3 py-2 text-sm rounded-lg border border-strong bg-surface-secondary text-heading placeholder:text-disabled outline-none resize-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              />
            )}
          </div>

          {/* 연결된 메모 */}
          <div className="py-3 border-b border-base">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3 text-sm text-body">
                <FileText className="w-4 h-4" />
                <span>연결된 메모</span>
              </div>
              <button
                onClick={() => setShowMemoLink(true)}
                className="text-xs text-primary-500 hover:text-primary-600"
              >
                + 연결
              </button>
            </div>
            {task.linkedMemos?.map((memo) => (
              <div
                key={memo.memoSyncId}
                className="flex items-center gap-2 p-2 rounded-lg border border-base border-l-4 mb-1.5 cursor-pointer hover-surface transition-colors"
                style={{ borderLeftColor: memo.memoColor }}
                onClick={() => setPreviewMemo(memo)}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-heading truncate">{memo.memoTitle}</p>
                  {memo.memoPreview && <p className="text-meta text-caption truncate mt-0.5">{memo.memoPreview}</p>}
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); handleUnlinkMemo(memo.memoSyncId) }}
                  className="p-1 text-disabled hover:text-red-500 flex-shrink-0"
                  title="연결 해제"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>

          {/* Attachment */}
          <div className="py-3">
            <AttachmentSection taskId={taskId} />
          </div>
        </div>
      </div>

      {/* 메모 연결 모달 */}
      <MemoLinkModal
        isOpen={showMemoLink}
        onClose={() => setShowMemoLink(false)}
        onSelect={handleLinkMemo}
        excludeIds={task?.linkedMemos?.map((m) => m.memoSyncId) || []}
      />

      {/* 메모 미리보기 */}
      {previewMemo && (
        <LinkedMemoPreview
          syncId={previewMemo.memoSyncId}
          title={previewMemo.memoTitle}
          color={previewMemo.memoColor}
          onClose={() => setPreviewMemo(null)}
        />
      )}
    </motion.div>
  )
}
